/** Render the bounded campus-safe rows returned for one automatic module scope. */
import { Loader2 } from 'lucide-react'

import type { KualiResearchSelectionPreview as SelectionPreview } from '../types'

export function KualiResearchSelectionPreview({
  preview,
  loading,
  error,
}: {
  preview: SelectionPreview | null
  loading: boolean
  error?: string
}) {
  if (error) {
    return <p role="alert" className="rounded-[9px] border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">{error}</p>
  }
  if (!preview && loading) {
    return (
      <div role="status" className="flex h-24 items-center justify-center rounded-[9px] border border-slate-200 bg-white text-xs text-slate-500">
        <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Checking UMCP awards…
      </div>
    )
  }
  if (!preview) return null

  const matchLabel = preview.truncated
    ? `${preview.matchCount.toLocaleString()}+`
    : preview.matchCount.toLocaleString()
  return (
    <section className="overflow-hidden rounded-[9px] border border-slate-200 bg-white">
      <header className="flex items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/70 px-3 py-2">
        <div>
          <h4 className="text-xs font-semibold text-slate-800">Live scope preview</h4>
          <p className="text-[11px] text-slate-500">
            <strong className="font-semibold text-slate-800">{matchLabel}</strong>{' '}
            campus-verified match{preview.matchCount === 1 ? '' : 'es'}
            {preview.truncated && ` · limit ${preview.limit.toLocaleString()} — narrow the scope`}
          </p>
        </div>
        {loading && <Loader2 aria-label="Updating preview" className="h-3.5 w-3.5 animate-spin text-slate-400" />}
      </header>
      {preview.rows.length === 0 ? (
        <p className="px-3 py-6 text-center text-xs text-slate-500">No campus-verified awards match this module scope.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] table-fixed text-left text-[11px]">
            <thead className="border-b border-slate-100 text-slate-500">
              <tr>
                <th className="w-[140px] px-3 py-2 font-medium">{preview.identifierLabel}</th>
                {preview.columns.map(column => <th key={column.key} className="w-[180px] px-3 py-2 font-medium">{column.label}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {preview.rows.map(row => (
                <tr key={row.identifier}>
                  <Cell value={row.identifier} />
                  {preview.columns.map(column => <Cell key={column.key} value={row.values[column.key] ?? ''} />)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {preview.skippedIdentifiers.length > 0 && (
        <div role="alert" className="border-t border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Skipped {preview.skippedIdentifiers.length} unreadable {preview.identifierLabel.toLowerCase()}{preview.skippedIdentifiers.length === 1 ? '' : 's'}: {preview.skippedIdentifiers.join(', ')}
        </div>
      )}
      {preview.rows.length > 0 && (
        <p className="border-t border-slate-100 px-3 py-2 text-[11px] text-slate-400">
          Showing the first {preview.rows.length} campus-verified match{preview.rows.length === 1 ? '' : 'es'}.
        </p>
      )}
    </section>
  )
}

function Cell({ value }: { value: string }) {
  return <td className="truncate px-3 py-2" title={value}>{value || '—'}</td>
}
